import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Edit, 
  Trash, 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  RefreshCw, 
  UserPlus,
  Eye,
  CheckCircle,
  XCircle,
  Filter,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Shield,
  Users
} from 'lucide-react';
import AdminApi from '../../apis/adminApis';
import UserDeleteConfirmModal from './modals/UserDeleteConfirmModal';
import UserRoleModal from './modals/UserRoleModal';
import UserStatusModal from './modals/UserStatusModal';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;
  status: string;
  created_at: string;
  last_login: string;
  max_upload_limit_mb: number;
  no_of_recipients: number;
  referral_count?: number; // Add referral count
  userProfile?: {
    temporaryUrl?: string;
  };
}

interface PaginationInfo {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

const UsersList: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0
  });
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('referral_count'); // Default sort by referral count
  const [sortDirection, setSortDirection] = useState<string>('desc'); // Default to descending (highest first)
  
  // Modals
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [roleModalOpen, setRoleModalOpen] = useState<boolean>(false);
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [menuOpenForUserId, setMenuOpenForUserId] = useState<number | null>(null);
  
  // Fetch users data
  const fetchUsers = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await AdminApi.getUsers({
        page,
        search: searchTerm,
        role: roleFilter,
        sort_by: sortBy,
        sort_direction: sortDirection,
        per_page: pagination.perPage
      });
      
      if (response.data.status === 'success') {
        const { data } = response.data;
        setUsers(data.data); // Pagination data structure
        setPagination({
          currentPage: data.current_page,
          lastPage: data.last_page,
          perPage: data.per_page,
          total: data.total
        });
      } else {
        setError('Failed to fetch users. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching users. Please try again.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load
  useEffect(() => {
    fetchUsers(1);
  }, []);
  
  // Handle search and filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1); // Reset to page 1 when filters change
    }, 500); // Debounce for better performance
    
    return () => clearTimeout(timer);
  }, [searchTerm, roleFilter, sortBy, sortDirection]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    fetchUsers(page);
  };
  
  // Handle sort change
  const handleSort = (field: string) => {
    if (field === sortBy) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection(field === 'referral_count' ? 'desc' : 'asc'); // Default to desc for referral count
    }
  };
  
  // Handle user deletion
  const handleDeleteUser = async (userId: number) => {
    try {
      const response = await AdminApi.deleteUser(userId);
      
      if (response.data.status === 'success') {
        // Remove user from the list
        setUsers(users.filter(user => user.id !== userId));
        setDeleteModalOpen(false);
        
        // If this was the last user on the page, go to previous page
        if (users.length === 1 && pagination.currentPage > 1) {
          fetchUsers(pagination.currentPage - 1);
        } else {
          // Refresh current page
          fetchUsers(pagination.currentPage);
        }
      } else {
        setError('Failed to delete user. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting user. Please try again.');
      console.error('Error deleting user:', err);
    }
  };
  
  // Handle user status change
  const handleToggleStatus = async (userId: number) => {
    try {
      const response = await AdminApi.changeUserStatus(userId);
      
      if (response.data.status === 'success') {
        // Update the user in the list
        setUsers(users.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              status: user.status === 'active' ? 'inactive' : 'active'
            };
          }
          return user;
        }));
        
        setStatusModalOpen(false);
      } else {
        setError('Failed to change user status. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error changing user status. Please try again.');
      console.error('Error changing user status:', err);
    }
  };
  
  // Handle user role change
  const handleChangeRole = async (userId: number, role: string) => {
    try {
      const response = await AdminApi.changeUserRole(userId, role);
      
      if (response.data.status === 'success') {
        // Update the user in the list
        setUsers(users.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              role
            };
          }
          return user;
        }));
        
        setRoleModalOpen(false);
      } else {
        setError('Failed to change user role. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error changing user role. Please try again.');
      console.error('Error changing user role:', err);
    }
  };
  
  // Get sort icon
  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4 ml-1" /> : 
      <ArrowDown className="h-4 w-4 ml-1" />;
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle click outside dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuOpenForUserId !== null) {
        const target = event.target as HTMLElement;
        const dropdown = document.getElementById(`dropdown-${menuOpenForUserId}`);
        
        if (dropdown && !dropdown.contains(target) && !target.closest('.dropdown-trigger')) {
          setMenuOpenForUserId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenForUserId]);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-xl font-semibold text-gray-800">Users Management</h2>
          <Link
            to="/admin/users/create"
            className="mt-3 md:mt-0 flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <UserPlus className="h-4 w-4" />
            <span>Add User</span>
          </Link>
        </div>
      </div>
      
      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            
            <div className="relative">
              <div className="flex items-center justify-center">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg w-full appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Roles</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
                <Filter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => fetchUsers(pagination.currentPage)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-600">
          {error}
        </div>
      )}
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <span>User</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center">
                  <span>Email</span>
                  {getSortIcon('email')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('role')}
              >
                <div className="flex items-center">
                  <span>Role</span>
                  {getSortIcon('role')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  <span>Status</span>
                  {getSortIcon('status')}
                </div>
              </th>
              {/* New Referrals Column */}
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('referral_count')}
              >
                <div className="flex items-center">
                  <span>Referrals</span>
                  {getSortIcon('referral_count')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center">
                  <span>Created</span>
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('last_login')}
              >
                <div className="flex items-center">
                  <span>Last Login</span>
                  {getSortIcon('last_login')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.userProfile?.temporaryUrl ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.userProfile.temporaryUrl}
                            alt={`${user.first_name} ${user.last_name}`}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-600 font-medium">
                              {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.phone_number || 'No phone'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                      user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.status === 'active' ? (
                        <CheckCircle className="mr-1 h-3 w-3" />
                      ) : (
                        <XCircle className="mr-1 h-3 w-3" />
                      )}
                      {user.status}
                    </span>
                  </td>
                  {/* Referral Count Cell */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                        user.referral_count && user.referral_count > 0 
                          ? 'bg-indigo-100 text-indigo-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <Users className="mr-1 h-3 w-3" />
                        {user.referral_count || 0}
                      </div>
                      {user.referral_count && user.referral_count > 0 && (
                        <Link 
                          to={`/admin/users/${user.id}?tab=referrals`}
                          className="ml-2 text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login ? formatDate(user.last_login) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2 relative">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="text-gray-600 hover:text-gray-900"
                        title="View"
                      >
                        <Eye className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/admin/users/${user.id}/edit`}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </Link>
                      <button
                        className="text-gray-600 hover:text-gray-900 dropdown-trigger"
                        title="More actions"
                        onClick={() => setMenuOpenForUserId(menuOpenForUserId === user.id ? null : user.id)}
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                      
                      {/* Dropdown menu */}
                      {menuOpenForUserId === user.id && (
                        <div 
                          id={`dropdown-${user.id}`}
                          className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200"
                        >
                          <button
                            className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedUser(user);
                              setStatusModalOpen(true);
                              setMenuOpenForUserId(null);
                            }}
                          >
                            {user.status === 'active' ? (
                              <XCircle className="mr-2 h-4 w-4 text-red-500" />
                            ) : (
                              <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                            )}
                            {user.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          
                          <button
                            className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedUser(user);
                              setRoleModalOpen(true);
                              setMenuOpenForUserId(null);
                            }}
                          >
                            <Shield className="mr-2 h-4 w-4 text-blue-500" />
                            Change Role
                          </button>
                          
                          <div className="border-t border-gray-100 my-1"></div>
                          
                          <button
                            className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteModalOpen(true);
                              setMenuOpenForUserId(null);
                            }}
                          >
                            <Trash className="mr-2 h-4 w-4 text-red-500" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {!loading && users.length > 0 && (
        <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === pagination.lastPage
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.perPage, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Pagination buttons */}
                {[...Array(pagination.lastPage)].map((_, i) => {
                  const page = i + 1;
                  // Show first page, last page, current page, and pages around current page
                  if (
                    page === 1 ||
                    page === pagination.lastPage ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === page
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                  
                  // Show ellipsis
                  if (
                    (page === 2 && pagination.currentPage > 3) ||
                    (page === pagination.lastPage - 1 && pagination.currentPage < pagination.lastPage - 2)
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.lastPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === pagination.lastPage
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {selectedUser && (
        <>
          <UserDeleteConfirmModal
            isOpen={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={() => handleDeleteUser(selectedUser.id)}
            user={selectedUser}
          />
          
          <UserRoleModal
            isOpen={roleModalOpen}
            onClose={() => setRoleModalOpen(false)}
            onConfirm={(role) => handleChangeRole(selectedUser.id, role)}
            user={selectedUser}
          />
          
          <UserStatusModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            onConfirm={() => handleToggleStatus(selectedUser.id)}
            user={selectedUser}
          />
        </>
      )}
    </div>
  );
};

export default UsersList;